/**
 * TweenExtensions - patches Tween.js to fix known bugs and add Genesys-specific functionality.
 * Overrides the .yoyo() method to work around the yoyo snap bug.
 */

import * as TWEEN from '@tweenjs/tween.js';

// Store the original yoyo method
const originalYoyo = TWEEN.Tween.prototype.yoyo;

// Store whether yoyo is enabled and the original duration/easing on the tween instance
declare module '@tweenjs/tween.js' {
  interface Tween<T> {
    _yoyoEnabled?: boolean;
    _originalDuration?: number;
    _originalEasing?: (t: number) => number;
  }
}

/**
 * Creates a yoyo-ified easing function that handles forward and backward motion.
 * First half (t: 0→0.5): applies easing going forward (scaled to 0→1).
 * Second half (t: 0.5→1): applies easing going backward (scaled back to 0→1).
 */
function createYoyoEasing(easingFn: (t: number) => number): (t: number) => number {
  return (t: number) => {
    if (t < 0.5) {
      // Forward: map 0→0.5 to 0→1 and apply easing
      return easingFn(2 * t);
    } else {
      // Backward: map 0.5→1 to 0→1, apply easing, then invert (1 - result)
      return 1 - easingFn(2 * (t - 0.5));
    }
  };
}

/** Override yoyo to use the easing workaround instead of the buggy built-in implementation. */
TWEEN.Tween.prototype.yoyo = function (this: any, yoyo?: boolean) {
  if (yoyo === undefined || yoyo === false) {
    // If disabling yoyo, restore original values if they exist
    if (this._yoyoEnabled && this._originalDuration !== undefined) {
      originalDuration.call(this, this._originalDuration);
      if (this._originalEasing) {
        originalEasing.call(this, this._originalEasing);
      }
    }
    this._yoyoEnabled = false;
    this._originalDuration = undefined;
    this._originalEasing = undefined;
    // Still call original to maintain any internal state
    return originalYoyo.call(this, false);
  }

  // Mark that yoyo is enabled
  this._yoyoEnabled = true;

  // Don't call the original yoyo as it's buggy - we'll handle it ourselves
  return this;
};

/**
 * Override duration to handle yoyo duration doubling.
 * When yoyo is enabled, we need to double the duration.
 */
const originalDuration = TWEEN.Tween.prototype.duration;
TWEEN.Tween.prototype.duration = function (this: any, d: number) {
  // Always store the original (non-doubled) duration
  this._originalDuration = d;

  // If yoyo is already enabled, double the duration now
  if (this._yoyoEnabled) {
    return originalDuration.call(this, d * 2);
  }

  return originalDuration.call(this, d);
};

/**
 * Override easing to wrap it with yoyo logic when yoyo is enabled.
 */
const originalEasing = TWEEN.Tween.prototype.easing;
TWEEN.Tween.prototype.easing = function (this: any, easingFn: (t: number) => number) {
  // Store the original easing function
  this._originalEasing = easingFn;

  // If yoyo is enabled, wrap the easing function
  if (this._yoyoEnabled) {
    return originalEasing.call(this, createYoyoEasing(easingFn));
  }

  return originalEasing.call(this, easingFn);
};

/**
 * Override start to apply yoyo modifications at start time if needed.
 * This ensures yoyo is properly configured even if .yoyo() is called after .duration() and .easing().
 */
const originalStart = TWEEN.Tween.prototype.start;
TWEEN.Tween.prototype.start = function (this: any, time?: number) {
  // If yoyo is enabled, ensure duration and easing are properly wrapped
  if (this._yoyoEnabled) {
    if (this._originalDuration !== undefined) {
      // Reapply duration with doubling
      originalDuration.call(this, this._originalDuration * 2);
    }
    // Reapply easing with yoyo wrapper if we have an original easing
    if (this._originalEasing) {
      originalEasing.call(this, createYoyoEasing(this._originalEasing));
    }
  }

  return originalStart.call(this, time);
};

